(function () {
  "use strict";

  const STORAGE_KEY = "fishystuff.language-preferences.v1";
  const LEGACY_UI_SETTINGS_KEY = "fishystuff.ui.settings.v1";
  const PROVIDER_SLOT = Symbol.for(
    "fishystuff.language-preference-local-provider.v1",
  );
  const LEGACY_PATHS = Object.freeze({
    locale: Object.freeze(["app", "language", "locale"]),
    dataLanguage: Object.freeze(["app", "language", "apiLang"]),
    serverDataspace: Object.freeze(["app", "game", "serviceRegion"]),
  });

  function isRecord(value) {
    if (!value || typeof value !== "object" || Array.isArray(value)) return false;
    const prototype = Object.getPrototypeOf(value);
    return prototype === null || Object.getPrototypeOf(prototype) === null;
  }

  function result(kind, fields = {}) {
    return Object.freeze({ kind, ...fields });
  }

  function getAtPath(root, path) {
    let current = root;
    for (const part of path) {
      if (!isRecord(current) || !Object.hasOwn(current, part)) {
        return Object.freeze({ present: false, value: undefined });
      }
      current = current[part];
    }
    return Object.freeze({ present: true, value: current });
  }

  function removePath(root, path) {
    if (!isRecord(root) || path.length === 0 || !Object.hasOwn(root, path[0])) {
      return root;
    }
    const next = { ...root };
    if (path.length === 1) {
      delete next[path[0]];
      return next;
    }
    const child = removePath(next[path[0]], path.slice(1));
    if (isRecord(child) && Object.keys(child).length > 0) {
      next[path[0]] = child;
    } else {
      delete next[path[0]];
    }
    return next;
  }

  function legacyCandidate(raw) {
    if (raw === null) return result("missing");
    let document;
    try {
      document = JSON.parse(raw);
    } catch (_error) {
      // The aggregate owns unrelated UI settings. Without the three exact
      // paths, this importer cannot claim a corrupt shared record.
      return result("missing");
    }
    if (!isRecord(document)) return result("missing");
    const fields = {};
    let present = false;
    for (const [field, path] of Object.entries(LEGACY_PATHS)) {
      const candidate = getAtPath(document, path);
      fields[field] = candidate;
      present ||= candidate.present;
    }
    return present
      ? result("candidate", { raw, fields: Object.freeze(fields) })
      : result("missing");
  }

  function createProvider() {
    let storage = null;
    try {
      storage = globalThis.localStorage ?? null;
    } catch (_error) {
      storage = null;
    }
    const eventTarget = window;
    const legacySettings = window.__fishystuffUiSettings ?? null;
    let disposed = false;
    const invalidationListeners = new Set();

    function readKey(key, operation = "read") {
      try {
        if (!storage || typeof storage.getItem !== "function") {
          throw new TypeError("Storage is unavailable.");
        }
        return result("available", { raw: storage.getItem(key) });
      } catch (_error) {
        return result("unavailable", {
          operation,
          reason: "storage-read-failed",
        });
      }
    }

    function writeKey(key, raw) {
      try {
        if (
          !storage
          || typeof storage.setItem !== "function"
          || typeof storage.getItem !== "function"
        ) {
          throw new TypeError("Storage is unavailable.");
        }
        storage.setItem(key, raw);
        if (storage.getItem(key) !== raw) {
          return result("unavailable", {
            operation: "write",
            reason: "storage-write-not-acknowledged",
          });
        }
        return result("written");
      } catch (_error) {
        return result("unavailable", {
          operation: "write",
          reason: "storage-write-failed",
        });
      }
    }

    function removeKey(key) {
      try {
        if (
          !storage
          || typeof storage.removeItem !== "function"
          || typeof storage.getItem !== "function"
        ) {
          throw new TypeError("Storage is unavailable.");
        }
        if (storage.getItem(key) === null) return result("removed");
        storage.removeItem(key);
        if (storage.getItem(key) !== null) {
          return result("unavailable", {
            operation: "remove",
            reason: "storage-remove-not-acknowledged",
          });
        }
        return result("removed");
      } catch (_error) {
        return result("unavailable", {
          operation: "remove",
          reason: "storage-remove-failed",
        });
      }
    }

    function readDocument() {
      return readKey(STORAGE_KEY);
    }

    function writeDocument(raw) {
      return writeKey(STORAGE_KEY, raw);
    }

    function removeDocument() {
      return removeKey(STORAGE_KEY);
    }

    function readLegacy() {
      const read = readKey(LEGACY_UI_SETTINGS_KEY, "legacy-read");
      if (read.kind === "unavailable") return read;
      return legacyCandidate(read.raw);
    }

    function legacyFieldsAbsent() {
      const read = readKey(LEGACY_UI_SETTINGS_KEY, "legacy-remove");
      if (read.kind === "unavailable") return read;
      return legacyCandidate(read.raw).kind === "missing"
        ? result("removed")
        : result("unavailable", {
          operation: "legacy-remove",
          reason: "legacy-fields-remove-not-acknowledged",
        });
    }

    function removeLegacy() {
      const before = readKey(LEGACY_UI_SETTINGS_KEY, "legacy-remove");
      if (before.kind === "unavailable") return before;
      const candidate = legacyCandidate(before.raw);
      if (candidate.kind === "missing") return result("removed");

      try {
        if (typeof legacySettings?.remove === "function") {
          for (const [field, path] of Object.entries(LEGACY_PATHS)) {
            if (candidate.fields[field].present) {
              legacySettings.remove(path, "language-preference-import");
            }
          }
        } else {
          let document = JSON.parse(before.raw);
          for (const path of Object.values(LEGACY_PATHS)) {
            document = removePath(document, path);
          }
          const persisted = Object.keys(document).length === 0
            ? removeKey(LEGACY_UI_SETTINGS_KEY)
            : writeKey(LEGACY_UI_SETTINGS_KEY, JSON.stringify(document));
          if (persisted.kind === "unavailable") return persisted;
        }
      } catch (_error) {
        return result("unavailable", {
          operation: "legacy-remove",
          reason: "legacy-fields-remove-failed",
        });
      }
      return legacyFieldsAbsent();
    }

    function subscribeInvalidation(listener) {
      if (typeof listener !== "function") {
        throw new TypeError("Provider invalidation listener must be a function.");
      }
      if (disposed) return () => {};
      invalidationListeners.add(listener);
      return () => invalidationListeners.delete(listener);
    }

    function invalidate() {
      if (disposed) return;
      for (const listener of invalidationListeners) {
        try {
          listener();
        } catch (error) {
          globalThis.reportError?.(error);
        }
      }
    }

    function handleStorage(event) {
      if (
        event
        && (
          event.key === STORAGE_KEY
          || event.key === LEGACY_UI_SETTINGS_KEY
          || event.key === null
        )
      ) {
        invalidate();
      }
    }

    function handlePageShow(event) {
      if (event?.persisted === true) invalidate();
    }

    eventTarget.addEventListener?.("storage", handleStorage);
    eventTarget.addEventListener?.("pageshow", handlePageShow);

    function dispose() {
      if (disposed) return;
      disposed = true;
      invalidationListeners.clear();
      eventTarget.removeEventListener?.("storage", handleStorage);
      eventTarget.removeEventListener?.("pageshow", handlePageShow);
    }

    return Object.freeze({
      dispose,
      readDocument,
      readLegacy,
      removeDocument,
      removeLegacy,
      subscribeInvalidation,
      writeDocument,
    });
  }

  Object.defineProperty(window, PROVIDER_SLOT, {
    configurable: true,
    value: createProvider,
    writable: false,
  });
})();
